import { useState } from "react";
import {
    AlertCircle,
    Key,
    Loader2,
    Plus,
    RefreshCw,
    Trash2
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createApiKey, getApiKeys, revokeApiKey, type ApiKey } from "@/api/portalApi";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function fmtDate(s: string | null) {
    if (!s) return "—";
    return new Date(s).toLocaleDateString("en-IN");
}

function RelativeTime({ value }: { value: string | null }) {
    if (!value) return <span className="text-muted-foreground">—</span>;
    const d = new Date(value);
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / 86400000);
    const label = days === 0 ? "Today" : days === 1 ? "Yesterday" : `${days}d ago`;
    return <span title={fmtDate(value)}>{label}</span>;
}

export function ApiKeysPage() {
    const { token } = useAuth();
    const qc = useQueryClient();
    const [keyName, setKeyName] = useState("");
    const [expiry, setExpiry] = useState("");
    const [newKey, setNewKey] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);

    const q = useQuery({ queryKey: ["api-keys"], queryFn: () => getApiKeys(token) });

    const createMut = useMutation({
        mutationFn: () => createApiKey(token, keyName.trim(), expiry ? parseInt(expiry) : undefined),
        onSuccess: data => {
            setNewKey(data.apiKey);
            setKeyName("");
            setExpiry("");
            void qc.invalidateQueries({ queryKey: ["api-keys"] });
        },
        onError: e => setErr(e instanceof Error ? e.message : "Failed to create key.")
    });

    const revokeMut = useMutation({
        mutationFn: (id: string) => revokeApiKey(token, id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
        onError: e => setErr(e instanceof Error ? e.message : "Failed to revoke key.")
    });

    return (
        <div className="mx-auto max-w-4xl space-y-6">
            <div>
                <h2 className="text-2xl font-bold">Plugin API Keys</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    API keys grant WHMCS, Plesk, and cPanel modules access to provision tenants and manage mailboxes.
                    Raw keys are shown once and never stored.
                </p>
            </div>

            {/* Create key form */}
            <div className="rounded-xl border bg-card p-5 space-y-4">
                <h3 className="text-sm font-semibold">Create New Key</h3>
                <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-48 space-y-1">
                        <Label>Key name</Label>
                        <Input
                            placeholder="e.g. WHMCS Production"
                            value={keyName}
                            onChange={e => setKeyName(e.target.value)}
                        />
                    </div>
                    <div className="w-40 space-y-1">
                        <Label>Expires in (days)</Label>
                        <Input
                            type="number"
                            min={1}
                            placeholder="Never"
                            value={expiry}
                            onChange={e => setExpiry(e.target.value)}
                        />
                    </div>
                    <div className="flex items-end">
                        <Button
                            onClick={() => createMut.mutate()}
                            disabled={createMut.isPending || !keyName.trim()}
                        >
                            {createMut.isPending
                                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                : <Plus className="mr-2 h-4 w-4" />}
                            Create
                        </Button>
                    </div>
                </div>

                {/* One-time key reveal */}
                {newKey && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-4 space-y-2 dark:bg-amber-900/20 dark:border-amber-800">
                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
                            <AlertCircle className="h-4 w-4" />
                            Copy this key now — it will not be shown again.
                        </div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 select-all break-all rounded bg-white px-3 py-2 text-sm font-mono dark:bg-gray-900">
                                {newKey}
                            </code>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => void navigator.clipboard.writeText(newKey)}
                            >
                                Copy
                            </Button>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setNewKey(null)} className="text-xs">
                            I've saved the key ✓
                        </Button>
                    </div>
                )}

                {err && (
                    <p className="text-xs text-red-600">{err}</p>
                )}
            </div>

            {/* Keys table */}
            {q.isLoading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Loading…
                </div>
            ) : q.error ? (
                <p className="text-sm text-red-600">Failed to load keys.</p>
            ) : (
                <div className="rounded-xl border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                            <tr>
                                <th className="px-4 py-3 text-left">Name</th>
                                <th className="px-4 py-3 text-left">Prefix</th>
                                <th className="px-4 py-3 text-left">Created</th>
                                <th className="px-4 py-3 text-left">Last used</th>
                                <th className="px-4 py-3 text-left">Expires</th>
                                <th className="px-4 py-3 text-left">Type</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {(q.data ?? []).map((k: ApiKey) => (
                                <tr key={k.id} className="hover:bg-muted/20">
                                    <td className="px-4 py-3 font-medium">{k.name}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{k.prefix}…</td>
                                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(k.createdAt)}</td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        <RelativeTime value={k.lastUsedAt} />
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(k.expiresAt)}</td>
                                    <td className="px-4 py-3">
                                        <span className={
                                            k.resellerId
                                                ? "rounded-full px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                                                : "rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                        }>
                                            {k.resellerId ? "Reseller" : "Admin"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-destructive hover:text-destructive"
                                            onClick={() => {
                                                if (confirm(`Revoke key "${k.name}"? This cannot be undone.`)) {
                                                    revokeMut.mutate(k.id);
                                                }
                                            }}
                                            disabled={revokeMut.isPending}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {!q.data?.length && (
                                <tr>
                                    <td colSpan={7} className="py-12 text-center">
                                        <Key className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
                                        <p className="text-muted-foreground">No API keys yet.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
